import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Activity, Ban, RefreshCw } from 'lucide-react';
import { useUrlFilters } from '../../../hooks/admin/useUrlFilters';
import { useSecurityOverview } from '../../../hooks/admin/useAdminSecurity';
import { enumField } from '../../../lib/adminFilters';
import { chartColors } from '../../../lib/chartColors';
import { getApiErrorMessage } from '../../../lib/apiError';
import { cn, formatDateTime } from '../../../lib/utils';
import Button from '../../../components/ui/Button';
import ExportJsonButton from '../../../components/admin/ExportJsonButton';
import StatTile from '../../../components/admin/StatTile';
import DateRangeFilter from '../../../components/admin/DateRangeFilter';
import { collectOverviewExport, securityExportFilename } from '../../../lib/adminSecurityExport';
import SeriesChart from '../../../components/admin/SeriesChart';
import InlineNotice from '../../../components/admin/InlineNotice';
import TrafficDrilldownModal from '../../../components/admin/TrafficDrilldownModal';
import { Pill, SeverityBadge } from '../../../components/admin/StatusBadge';
import BlockIpDialog from './BlockIpDialog';
import type { SecurityEvent, TrafficScope } from '../../../types/admin';
import type { SecurityTab } from './tabs';

const HOURS_OPTIONS = [
  { value: '1', label: '1h' },
  { value: '6', label: '6h' },
  { value: '24', label: '24h' },
  { value: '168', label: '7d' },
] as const;

const overviewSchema = {
  hours: enumField(['1', '6', '24', '168'], '24'),
};

function shortTs(hours: number) {
  return (value: string) => {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return hours > 24
      ? d.toLocaleString('en-MY', { month: 'short', day: 'numeric', hour: '2-digit' })
      : d.toLocaleTimeString('en-MY', { hour: '2-digit', minute: '2-digit' });
  };
}

function pct(part: number, total: number): string {
  if (!total) return '0.0%';
  return `${((part / total) * 100).toFixed(part / total < 0.001 && part > 0 ? 2 : 1)}%`;
}

function RecentEvents({ title, events, empty }: { title: string; events: SecurityEvent[]; empty: string }) {
  return (
    <div className="glass-card p-5">
      <h3 className="text-sm font-semibold text-glass-text">{title}</h3>
      {events.length === 0 ? (
        <p className="mt-3 text-sm text-glass-text-3">{empty}</p>
      ) : (
        <ul className="mt-3 divide-y divide-glass">
          {events.slice(0, 10).map((e) => (
            <li key={e.id} className="py-2 text-sm">
              <div className="flex items-center justify-between gap-2">
                <span className="truncate font-mono text-xs text-glass-text-2" title={e.route ?? undefined}>
                  {e.route || e.type}
                </span>
                <span className="shrink-0 text-xs text-glass-text-3">{formatDateTime(e.createdAt)}</span>
              </div>
              <div className="mt-0.5 flex items-center gap-2">
                <SeverityBadge severity={e.severity} />
                <span className="truncate text-glass-text" title={e.message}>
                  {e.message}
                </span>
              </div>
              {e.ip && <div className="mt-0.5 font-mono text-[11px] text-glass-text-3">{e.ip}</div>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function OverviewTab({ onNavigateTab }: { onNavigateTab: (tab: SecurityTab) => void }) {
  const { filters, setFilters } = useUrlFilters(overviewSchema);
  const hours = Number(filters.hours);
  const query = useSecurityOverview(hours);
  const data = query.data;
  const totals = data?.totals;

  const [drill, setDrill] = useState<{ scope: TrafficScope; label: string } | null>(null);
  const [blockIp, setBlockIp] = useState<{ ip: string; reason: string } | null>(null);

  const updatedAt = query.dataUpdatedAt
    ? new Date(query.dataUpdatedAt).toLocaleTimeString('en-MY', { hour: '2-digit', minute: '2-digit' })
    : null;

  const chartSeries = useMemo(
    () => [
      { key: 's2xx', name: '2xx', color: chartColors.pos },
      { key: 's4xx', name: '4xx', color: chartColors.warn },
      { key: 's429', name: '429', color: chartColors.info },
      { key: 's5xx', name: '5xx', color: chartColors.danger },
    ],
    []
  );

  const errorRate = totals ? (totals.requests > 0 ? totals.s5xx / totals.requests : 0) : 0;
  const failed15m = data?.failedLogins.last15m ?? 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <DateRangeFilter value={filters.hours} options={HOURS_OPTIONS} onChange={(v) => setFilters({ hours: v })} label="Window" />
        <div className="flex flex-wrap items-center justify-end gap-3">
          {updatedAt && <span className="text-xs text-glass-text-3">Updated {updatedAt} · auto-refresh 60s</span>}
          <ExportJsonButton
            label="Download this tab"
            filename={() => securityExportFilename('overview', hours)}
            collect={() => collectOverviewExport(hours)}
          />
          <Button variant="outline" size="sm" onClick={() => void query.refetch()} disabled={query.isFetching}>
            <RefreshCw className={cn('mr-2 h-4 w-4', query.isFetching && 'animate-spin')} aria-hidden />
            Refresh
          </Button>
        </div>
      </div>

      {query.isError && <InlineNotice tone="error">{getApiErrorMessage(query.error, 'Failed to load the security overview')}</InlineNotice>}

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
        <StatTile label="Requests" value={(totals?.requests ?? 0).toLocaleString()} loading={query.isPending} sub={`avg ${Math.round(totals?.avgDurationMs ?? 0)} ms`} />
        <StatTile
          label="Error rate (5xx)"
          value={pct(totals?.s5xx ?? 0, totals?.requests ?? 0)}
          tone={errorRate > 0.01 ? 'danger' : (totals?.s5xx ?? 0) > 0 ? 'warn' : 'pos'}
          loading={query.isPending}
          sub={`${(totals?.s5xx ?? 0).toLocaleString()} errors · ${(totals?.s4xx ?? 0).toLocaleString()} 4xx`}
        />
        <StatTile
          label="Rate limited (429)"
          value={(totals?.s429 ?? 0).toLocaleString()}
          tone={(totals?.s429 ?? 0) > 0 ? 'warn' : 'default'}
          loading={query.isPending}
        />
        <StatTile
          label="Failed logins"
          value={
            <span>
              {failed15m}
              <span className="text-base text-glass-text-3"> / {data?.failedLogins.last24h ?? 0}</span>
            </span>
          }
          tone={failed15m > 0 ? 'danger' : 'default'}
          loading={query.isPending}
          sub="last 15 min / 24 h"
          onClick={() => onNavigateTab('auth')}
        />
        <StatTile
          label="Open alerts"
          value={(data?.openAlerts ?? 0).toLocaleString()}
          tone={(data?.openAlerts ?? 0) > 0 ? 'warn' : 'default'}
          loading={query.isPending}
          sub={`${data?.unresolvedCritical ?? 0} unresolved critical events`}
          onClick={() => onNavigateTab('alerts')}
        />
        <StatTile
          label="Blocked IPs"
          value={(data?.blockedIps ?? 0).toLocaleString()}
          loading={query.isPending}
          onClick={() => onNavigateTab('blocked')}
          sub={data?.selfIp ? `you: ${data.selfIp}` : undefined}
        />
      </div>

      <div className="glass-card p-5">
        <h3 className="mb-3 text-sm font-semibold text-glass-text">Requests by status</h3>
        <SeriesChart data={data?.series ?? []} xKey="ts" xFormatter={shortTs(hours)} series={chartSeries} loading={query.isPending} height={280} />
      </div>

      <div className="glass-card overflow-hidden">
        <div className="flex items-center justify-between border-b border-glass px-5 py-3">
          <h3 className="text-sm font-semibold text-glass-text">Top IPs</h3>
          <span className="text-xs text-glass-text-3">by request count</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-glass-1 text-left text-[0.7rem] uppercase tracking-[0.05em] text-glass-text-2">
              <tr>
                <th className="px-5 py-2 font-medium">IP</th>
                <th className="px-3 py-2 font-medium">Requests</th>
                <th className="px-3 py-2 font-medium">Users</th>
                <th className="px-3 py-2 font-medium">4xx</th>
                <th className="px-3 py-2 font-medium">429</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-5 py-2 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-glass">
              {query.isPending ? (
                <tr>
                  <td colSpan={7} className="px-5 py-6 text-center text-glass-text-3">Loading…</td>
                </tr>
              ) : (data?.topIps ?? []).length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-5 py-6 text-center text-glass-text-3">No traffic recorded in this window.</td>
                </tr>
              ) : (
                (data?.topIps ?? []).map((row) => (
                  <tr key={row.ip} className="hover:bg-glass-3/40">
                    <td className="px-5 py-2.5 font-mono text-xs text-glass-text">
                      {row.ip}
                      {data?.selfIp === row.ip && <span className="ml-2 text-[11px] text-glass-text-3">(you)</span>}
                    </td>
                    <td className="px-3 py-2.5 tabular-nums">{row.count.toLocaleString()}</td>
                    <td className="px-3 py-2.5 tabular-nums text-glass-text-2">{row.distinctUsers}</td>
                    <td className={cn('px-3 py-2.5 tabular-nums', row.s4xx > 0 && 'text-[rgb(var(--warn))]')}>{row.s4xx}</td>
                    <td className={cn('px-3 py-2.5 tabular-nums', row.s429 > 0 && 'text-[rgb(var(--info))]')}>{row.s429}</td>
                    <td className="px-3 py-2.5">{row.blocked ? <Pill tone="danger">blocked</Pill> : <span className="text-xs text-glass-text-3">—</span>}</td>
                    <td className="px-5 py-2.5">
                      <div className="flex justify-end gap-1.5">
                        <button
                          type="button"
                          onClick={() => setDrill({ scope: { ip: row.ip }, label: `IP ${row.ip}` })}
                          className="btn-glass inline-flex items-center gap-1 px-2.5 py-1 text-xs"
                        >
                          <Activity className="h-3.5 w-3.5" aria-hidden />
                          Traffic
                        </button>
                        {!row.blocked && (
                          <button
                            type="button"
                            onClick={() =>
                              setBlockIp({
                                ip: row.ip,
                                reason: `Excessive traffic: ${row.count.toLocaleString()} requests in ${hours}h (${row.s4xx} 4xx, ${row.s429} rate-limited)`,
                              })
                            }
                            className="btn-glass inline-flex items-center gap-1 px-2.5 py-1 text-xs text-[rgb(var(--danger))]"
                          >
                            <Ban className="h-3.5 w-3.5" aria-hidden />
                            Block
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="glass-card overflow-hidden">
          <div className="border-b border-glass px-5 py-3">
            <h3 className="text-sm font-semibold text-glass-text">Top users</h3>
          </div>
          <table className="w-full text-sm">
            <tbody className="divide-y divide-glass">
              {(data?.topUsers ?? []).length === 0 && !query.isPending && (
                <tr>
                  <td className="px-5 py-6 text-center text-glass-text-3">No authenticated traffic.</td>
                </tr>
              )}
              {(data?.topUsers ?? []).map((u) => (
                <tr key={u.userId} className="hover:bg-glass-3/40">
                  <td className="px-5 py-2.5">
                    {u.email ? (
                      <Link to={`/admin/users?search=${encodeURIComponent(u.email)}`} className="text-glass-text hover:text-accent2">
                        {u.fullName || u.email}
                      </Link>
                    ) : (
                      <span className="text-glass-text">{u.fullName || u.userId}</span>
                    )}
                    <div className="text-xs text-glass-text-3">{u.orgName || u.email || '—'}</div>
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{u.count.toLocaleString()}</td>
                  <td className="px-5 py-2.5 text-right">
                    <button
                      type="button"
                      onClick={() => setDrill({ scope: { userId: u.userId }, label: u.email || u.fullName || u.userId })}
                      className="btn-glass inline-flex items-center gap-1 px-2.5 py-1 text-xs"
                    >
                      <Activity className="h-3.5 w-3.5" aria-hidden />
                      Traffic
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="glass-card overflow-hidden">
          <div className="border-b border-glass px-5 py-3">
            <h3 className="text-sm font-semibold text-glass-text">Top organisations</h3>
          </div>
          <table className="w-full text-sm">
            <tbody className="divide-y divide-glass">
              {(data?.topOrgs ?? []).length === 0 && !query.isPending && (
                <tr>
                  <td className="px-5 py-6 text-center text-glass-text-3">No organisation traffic.</td>
                </tr>
              )}
              {(data?.topOrgs ?? []).map((o) => (
                <tr key={o.organizationId} className="hover:bg-glass-3/40">
                  <td className="px-5 py-2.5 text-glass-text">
                    {o.name || o.organizationId}
                    <div className="font-mono text-[11px] text-glass-text-3">{o.organizationId}</div>
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{o.count.toLocaleString()}</td>
                  <td className="px-5 py-2.5 text-right">
                    <button
                      type="button"
                      onClick={() => setDrill({ scope: { organizationId: o.organizationId }, label: o.name || o.organizationId })}
                      className="btn-glass inline-flex items-center gap-1 px-2.5 py-1 text-xs"
                    >
                      <Activity className="h-3.5 w-3.5" aria-hidden />
                      Traffic
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <RecentEvents title="Recent 5xx" events={data?.recent5xx ?? []} empty="No server errors in this window." />
        <RecentEvents title="Recent 429" events={data?.recent429 ?? []} empty="No rate-limit hits in this window." />
      </div>

      {data?.failedLogins.topIps && data.failedLogins.topIps.length > 0 && (
        <div className="glass-card p-5">
          <h3 className="text-sm font-semibold text-glass-text">Failed logins by IP (24h)</h3>
          <ul className="mt-3 flex flex-wrap gap-2">
            {data.failedLogins.topIps.map((row) => (
              <li key={row.ip} className="inline-flex items-center gap-2 rounded-lg border border-glass bg-glass-2 px-2.5 py-1 text-xs">
                <span className="font-mono text-glass-text">{row.ip}</span>
                <span className="tabular-nums text-[rgb(var(--danger))]">{row.count}</span>
                <button
                  type="button"
                  onClick={() => setBlockIp({ ip: row.ip, reason: `${row.count} failed logins in 24h` })}
                  className="text-glass-text-3 hover:text-[rgb(var(--danger))]"
                  title="Block this IP"
                >
                  <Ban className="h-3.5 w-3.5" aria-hidden />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <TrafficDrilldownModal
        scope={drill?.scope ?? null}
        label={drill?.label}
        onClose={() => setDrill(null)}
        onBlockIp={(ip) => {
          setDrill(null);
          setBlockIp({ ip, reason: 'Suspicious traffic pattern (see drilldown)' });
        }}
      />
      <BlockIpDialog
        key={blockIp?.ip ?? 'none'}
        ip={blockIp?.ip ?? null}
        initialReason={blockIp?.reason}
        selfIp={data?.selfIp ?? null}
        onClose={() => setBlockIp(null)}
      />
    </div>
  );
}
